import { Button } from "@jace/ui";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { RendererProps } from "./types";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

type PasswordCallback = (password: string | null) => void;

// NOTE: (bwilytsch): not sure if a CDN is the best option here
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
	pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
}

type ChildrenProps = {
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
};

type PdfRendererProps = RendererProps & {
	children?: (props: ChildrenProps) => ReactElement;
};

export function PdfRenderer({
	url,
	zoom,
	children,
}: PdfRendererProps): ReactElement {
	const [numPages, setNumPages] = useState<number>(1);
	const [currentPage, setCurrentPage] = useState(1);
	const [pdfWidth, setPdfWidth] = useState(
		Math.min(1200, window.innerWidth * 0.8),
	);
	const [error, setError] = useState<string | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const pdfOptions = useMemo(
		() => ({
			standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
			cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
			cMapPacked: true,
		}),
		[],
	);

	useEffect(() => {
		const handleResize = (): void => {
			setPdfWidth(Math.min(1200, window.innerWidth * 0.8));
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const pageNum = Number.parseInt(
							entry.target.getAttribute("data-page-number") || "1",
						);
						setCurrentPage(pageNum);
					}
				});
			},
			{
				root: container,
				threshold: 0.5,
			},
		);

		const pages = container.querySelectorAll("[data-page-number]");
		pages.forEach((page) => observer.observe(page));

		return () => {
			pages.forEach((page) => observer.unobserve(page));
		};
	}, [numPages]);

	const handlePassword = (
		callback: PasswordCallback,
		_reason: number,
	): void => {
		const password = prompt(
			"This PDF is password protected. Please enter the password:",
		);

		if (password === null) {
			// User clicked Cancel
			setError("Password required to view this PDF");
			callback(null);
			return;
		}

		callback(password);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		const container = containerRef.current;
		if (!container) return;

		const pageElement = container.querySelector(`[data-page-number="${page}"]`);
		if (pageElement) {
			pageElement.scrollIntoView({ block: "start" });
		}
	};

	if (error) {
		return (
			<div className="flex h-[calc(90vh-100px)] items-center justify-center">
				<div className="text-center text-text-error">
					<p>{error}</p>
					<Button
						className="mt-4"
						onClick={() => {
							setError(null);
						}}
					>
						Try Again
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex w-full flex-col items-center">
			<div
				ref={containerRef}
				className="h-[calc(90vh-100px)] w-full overflow-y-auto overflow-x-hidden"
			>
				<Document
					className="flex w-full flex-col items-center py-4"
					file={url}
					onLoadError={() => setError("Failed to load PDF")}
					onLoadSuccess={({ numPages }) => {
						setNumPages(numPages);
						setError(null);
					}}
					options={pdfOptions}
				>
					{Array.from(new Array(numPages), (_, index) => (
						<div
							key={`page-container-${index + 1}`}
							className="mb-8 flex flex-col items-center"
							data-page-number={index + 1}
						>
							<div className="w-fit">
								<Page
									key={`page-${index + 1}`}
									pageNumber={index + 1}
									width={pdfWidth * zoom}
									renderTextLayer
									renderAnnotationLayer
								/>
							</div>
						</div>
					))}
				</Document>
			</div>
			{children?.({
				currentPage,
				totalPages: numPages,
				onPageChange: handlePageChange,
			})}
		</div>
	);
}
